// Or from '@reduxjs/toolkit/query' if not using the auto-generated hooks
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { apiConfig } from '../../../config'
import { getHeaders, ApiScope } from '../../silentAuth'
import { BacnetObjectMappingConfiguration, BacnetPropertyIds, CreateObjectMappingCommand } from './types'
import { BacnetObjectTypes } from './types'
import { cacher } from "../../rtkQueryCacheUtils";
import { createSelector } from '@reduxjs/toolkit'


export type GetMappingTemplates = {
  edgeDeviceId: string,
  deviceId: number,
}

export type InitializeDeviceArgs = {
  edgeDeviceId: string,
  deviceId: number,
}

export type WriteBacnetValueCommand = {
  edgeDeviceId: string,
  deviceId: number,
  objectType: BacnetObjectTypes,
  objectId: number,
  propertyId: BacnetPropertyIds,
  value: any,
  priority?: number,
  arrayIndex?: number
}

export type MappingTemplate = {
  name: string,
  fullName: string,
  description: string,
  vendors: number[]
}

export type RunMappingTemplateCommand = {
  template: string,
  edgeDeviceId: string,
  bacnetDeviceId: number,
  parentId: string,
  resourceGroupId: string,
  selectedObjects: SelectedBacnetObject[]
}
export type SelectedBacnetObject = {
  type: BacnetObjectTypes,
  instanceId: number
}


// Define a service using a base URL and expected endpoints
export const bacnetMappingApi = createApi({
  reducerPath: 'bacnetMappingApi',
  tagTypes: [...cacher.defaultTags, 'BacnetObjectMappings'],
  baseQuery: fetchBaseQuery({
    baseUrl: apiConfig.apiBaseUrl,
    prepareHeaders: async (headers, { getState }) => { return await getHeaders(ApiScope, headers); }
  }),
  endpoints: (builder) => ({
    getMappingsFromObjectId: builder.query<BacnetObjectMappingConfiguration[], string>({
      query: (objectid) => `/mappings/v2/objectmappings?objectId=${objectid}`,
      providesTags: cacher.providesList('BacnetObjectMappings'),
    }),
    getMappingsFromDevice: builder.query<BacnetObjectMappingConfiguration[], { edgeDeviceId: string, bacnetDeviceId: number }>({
      query: ({ edgeDeviceId, bacnetDeviceId }) => `/mappings/v2/objectmappings?edgedeviceId=${edgeDeviceId}&bacnetDeviceId=${bacnetDeviceId}`,
      providesTags: cacher.providesList('BacnetObjectMappings'),
    }),

    createMapping: builder.mutation<BacnetObjectMappingConfiguration, CreateObjectMappingCommand>({
      query: (obj) => ({
        url: `/mappings/mappings/bacnet/new`,
        method: 'post',
        body: obj
      }),
      invalidatesTags: cacher.invalidatesList("BacnetObjectMappings")
    }),
    deleteMapping: builder.mutation<any, string>({
      query: (id) => ({
        url: `/mappings/mappings/bacnet/${id}`,
        method: 'delete',
      }),
      invalidatesTags: cacher.invalidatesList("BacnetObjectMappings")
    }),
    initializeDevice: builder.mutation<any, InitializeDeviceArgs>({
      query: ({ edgeDeviceId, deviceId }) => ({
        url: `/mappings/mappings/${edgeDeviceId}/${deviceId}/initialize`,
        method: 'post',
      }),
    }),
    syncDeviceTime: builder.mutation<any, { edgeDeviceId: string, deviceId: number }>({
      query: ({ edgeDeviceId, deviceId }) => ({
        url: `/mappings/mappings/${edgeDeviceId}/${deviceId}/synctime`,
        method: 'post',
      }),
    }),
    writeBacnetValue: builder.mutation<any, WriteBacnetValueCommand>({
      query: (args) => ({
        url: `/mappings/mappings/${args.edgeDeviceId}/${args.deviceId}/${args.objectType}/${args.objectId}/${args.propertyId}`,
        method: 'post',
        body: args
      }),
    }),

    getMappingsTemplates: builder.query<MappingTemplate[], GetMappingTemplates>({
      query: ({ edgeDeviceId, deviceId }) => `/mappings/mappings/bacnet/${edgeDeviceId}/${deviceId}/templates`,
    }),
    runMappingTemplate: builder.mutation<any, RunMappingTemplateCommand>({
      query: (args) => ({
        url: `/mappings/mappings/bacnet/${args.edgeDeviceId}/${args.bacnetDeviceId}/templates`,
        method: 'post',
        body: args
      }),
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetMappingsFromObjectIdQuery,
  useCreateMappingMutation,
  useDeleteMappingMutation,
  useInitializeDeviceMutation,
  useGetMappingsTemplatesQuery,
  useRunMappingTemplateMutation,
  useWriteBacnetValueMutation,
  useSyncDeviceTimeMutation,
  useGetMappingsFromDeviceQuery
} = bacnetMappingApi
